import React, { useState, useEffect, useCallback } from "react";
import { ExternalLink, ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from "lucide-react";
import Cookies from "js-cookie";
import OrgDirectoryModal from "./orgdirectorymodal.js";

const MiniBar = ({ color, value, max }) => (
  <div className="mini-bar">
    {Array.from({ length: 15 }, (_, i) => (
      <div
        key={i}
        className="mini-bar__segment"
        style={{
          background: i < Math.round((value / max) * 15) ? color : `${color}33`,
        }}
      />
    ))}
  </div>
);

const STATIC_ALERTS = [
  {
    id: 1,
    org: "ConvertML",
    projectId: "690ce3c2d4d1e6cb47d35a85_99b2534d-6b3a-4362-8350-bf6b4c05464e",
    error: "Banner Book Gen Failed",
  },
  {
    id: 2,
    org: "ConvertML",
    projectId: "690ce3c2d4d1e6cb47d35a85_99b2534d-6b3a-4362-8350-bf6b4c05464e",
    error: "Banner Book Gen Failed",
  },
  {
    id: 3,
    org: "ConvertML",
    projectId: "690ce3c2d4d1e6cb47d35a85_99b2534d-6b3a-4362-8350-bf6b4c05464e",
    error: "Banner Book Gen Failed",
  },
];

const statusBadgeClass = (s = "") => {
  if (s === "running") return "badge badge--status badge--running";
  if (s === "failed" || s === "error") return "badge badge--status badge--failed";
  if (s === "completed" || s === "success") return "badge badge--status badge--completed";
  return "badge badge--status badge--default";
};

/* ── Compact pagination used in org dashboard table ── */
const TablePagination = ({ pagination, onPageChange }) => {
  if (!pagination || pagination.totalPages <= 1) return null;
  const { page, totalPages } = pagination;
  return (
    <div className="table-pagination">
      <button
        className="table-pg-btn"
        disabled={page === 1}
        onClick={() => onPageChange(page - 1)}
      >
        <ChevronLeft size={13} />
      </button>
      <span className="table-pg-info">
        {page} / {totalPages}
      </span>
      <button
        className="table-pg-btn"
        disabled={page === totalPages}
        onClick={() => onPageChange(page + 1)}
      >
        <ChevronRight size={13} />
      </button>
    </div>
  );
};

/* Project columns — no Org Id */
const PROJECT_COLS = [
  { label: "Org Name",        key: "org_name" },
  { label: "Project Id",      key: "project_id",      mono: true, maxw: "td--maxw160" },
  { label: "Survey Id",       key: "survey_id",       mono: true, maxw: "td--maxw120" },
  { label: "Survey Name",     key: "survey_name",                 maxw: "td--maxw180" },
  { label: "Audience Grp Id", key: "audience_grp_id", mono: true, maxw: "td--maxw180" },
];

const DASHBOARD_PROJECT_LIMIT = 5;   // rows shown before "Load More"
const LOAD_MORE_STEP = 10;           // rows added per "Load More" click

const SuperadminDashboard = () => {
  /* ── Orgs ── */
  const [orgs, setOrgs] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(true);
  const [orgsPagination, setOrgsPagination] = useState({
    page: 1, limit: 5, total: 0, totalPages: 1,
  });
  const [showOrgModal, setShowOrgModal] = useState(false);

  /* ── Selected org (shared between dashboard & modal) ── */
  const [selectedOrg, setSelectedOrg] = useState(null);

  /* ── Projects (driven by selected org) ── */
  const [allProjects, setAllProjects] = useState([]);   // all fetched projects
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [visibleCount, setVisibleCount] = useState(DASHBOARD_PROJECT_LIMIT);
  const [projectsTotalFromApi, setProjectsTotalFromApi] = useState(0);

  /* ── Fetch orgs (limit 5 for dashboard) ── */
  const fetchOrgs = useCallback(async (page = 1) => {
    setOrgsLoading(true);
    try {
      const token = Cookies.get("token");
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/superadmin/orgs?page=${page}&limit=5`,
        { headers: { token, "Content-Type": "application/json" } }
      );
      const json = await res.json();
      if (json.success) {
        setOrgs(json.data);
        setOrgsPagination(json.pagination);
      }
    } catch (e) {
      console.error("Failed to fetch orgs", e);
    } finally {
      setOrgsLoading(false);
    }
  }, []);

  /* ── Fetch ALL projects for org (single large fetch for inline expand) ── */
  const fetchOrgProjects = useCallback(async (orgId) => {
    if (!orgId) return;
    setProjectsLoading(true);
    setVisibleCount(DASHBOARD_PROJECT_LIMIT);
    try {
      const token = Cookies.get("token");
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/superadmin/orgs/${orgId}/projects?page=1&limit=200`,
        { headers: { token, "Content-Type": "application/json" } }
      );
      const json = await res.json();
      if (json.success) {
        setAllProjects(json.data);
        setProjectsTotalFromApi(json.pagination?.total ?? json.data.length);
      }
    } catch (e) {
      console.error("Failed to fetch org projects", e);
    } finally {
      setProjectsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrgs();
  }, [fetchOrgs]);

  const handleSelectOrg = useCallback((org) => {
    setSelectedOrg(org);
    fetchOrgProjects(org._id);
  }, [fetchOrgProjects]);

  const handleOrgPageChange = useCallback((page) => {
    fetchOrgs(page);
  }, [fetchOrgs]);

  const visibleProjects = allProjects.slice(0, visibleCount);
  const hasMore = visibleCount < allProjects.length;

  return (
    <div className="dashboard">
      <div className="dashboard__header">
        <h1 className="dashboard__title">
          Good to see you, <strong>Superadmin!</strong>
        </h1>
      </div>

      <div className="dashboard__body">

        {/* ── Pipeline Health ── */}
        <div className="card">
          <h2 className="card__title">Global Pipeline Health</h2>
          <p className="card__subtitle">A real-time status card for all current pipeline runs.</p>
          <div className="pipeline-grid">
            <div className="pipeline-stat pipeline-stat--blue">
              <div className="pipeline-stat__label">Running</div>
              <div className="pipeline-stat__value">25</div>
              <MiniBar color="#4a9eff" value={25} max={300} />
            </div>
            <div className="pipeline-stat pipeline-stat--green">
              <div className="pipeline-stat__label">Succeeded</div>
              <div className="pipeline-stat__value">234</div>
              <MiniBar color="#4adf9a" value={234} max={300} />
            </div>
            <div className="pipeline-stat pipeline-stat--red">
              <div className="pipeline-stat__label">Failed</div>
              <div className="pipeline-stat__value">2</div>
              <MiniBar color="#f87171" value={2} max={300} />
            </div>
          </div>
        </div>

        {/* ── Two-col: Alerts + Org Directory ── */}
        <div className="two-col">

          {/* Critical Alerts */}
          <div className="card">
            <div className="card__header">
              <h2 className="card__title">Critical Alerts</h2>
              <span className="badge--alert">5 Active</span>
            </div>
            <table className="data-table data-table--inline">
              <colgroup>
                <col style={{ width: "22%" }} />
                <col style={{ width: "36%" }} />
                <col style={{ width: "28%" }} />
                <col style={{ width: "14%" }} />
              </colgroup>
              <thead>
                <tr>
                  {["Org Name", "Project ID", "Error", "Action"].map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {STATIC_ALERTS.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <span className="org-name-cell">
                        <div className="alert-bar" />
                        {a.org}
                      </span>
                    </td>
                    <td className="td--mono td--maxw120">
                      <div className="td--truncate">{a.projectId}</div>
                    </td>
                    <td style={{ color: "var(--color-red)", fontSize: 12 }}>{a.error}</td>
                    <td>
                      <button className="btn-logs">View Logs</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Org Directory — 5 rows */}
          <div className="card">
            <div className="card__header">
              <h2 className="card__title">Organization & Admin Directory</h2>
              <button
                className="card__icon-btn"
                onClick={() => setShowOrgModal(true)}
                title="Expand"
              >
                <ExternalLink size={16} />
              </button>
            </div>
            <table className="data-table data-table--inline data-table--clickable">
              <colgroup>
                <col style={{ width: "28%" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "14%" }} />
              </colgroup>
              <thead>
                <tr>
                  {["Org Name", "Org Id", "Projects", "Members"].map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orgsLoading ? (
                  <tr>
                    <td colSpan={4} className="text-empty">Loading…</td>
                  </tr>
                ) : orgs.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-empty">No organizations found</td>
                  </tr>
                ) : (
                  orgs.map((org) => (
                    <tr
                      key={org._id}
                      onClick={() => handleSelectOrg(org)}
                      className={selectedOrg?._id === org._id ? "org-row--selected" : ""}
                      style={{ cursor: "pointer" }}
                    >
                      <td>
                        <span className="org-name-cell org-name-cell--sm">
                          {org.org_name}
                        </span>
                      </td>
                      <td className="td--mono">{org._id?.toString().slice(-8).toUpperCase()}</td>
                      <td>{org.projectCount ?? "—"}</td>
                      <td>{org.memberCount ?? "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            <TablePagination pagination={orgsPagination} onPageChange={handleOrgPageChange} />
          </div>
        </div>

        {/* ── Global Project Registry — inline, no modal ── */}
        <div className="card card--last">
          <div className="card__header">
            <div>
              <h2 className="card__title">Global Project Registry</h2>
              {selectedOrg && (
                <p className="card__subtitle" style={{ margin: "4px 0 0" }}>
                  Showing projects for <strong style={{ color: "#fff" }}>{selectedOrg.org_name}</strong>
                  {allProjects.length > 0 && (
                    <span style={{ color: "#64748b", marginLeft: 8 }}>
                      ({Math.min(visibleCount, allProjects.length)} of {allProjects.length})
                    </span>
                  )}
                </p>
              )}
            </div>
          </div>

          <div className="table-wrap">
            <table className="data-table data-table--inline data-table--min900">
              <colgroup>
                <col style={{ width: "14%" }} />
                <col style={{ width: "20%" }} />
                <col style={{ width: "16%" }} />
                <col style={{ width: "20%" }} />
                <col style={{ width: "16%" }} />
                <col style={{ width: "8%" }} />
                <col style={{ width: "6%" }} />
              </colgroup>
              <thead>
                <tr>
                  {[...PROJECT_COLS.map(c => c.label), "Status", "Action"].map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {!selectedOrg ? (
                  <tr>
                    <td colSpan={7} className="text-empty text-empty--hint">
                      ← Click an organization row above to see its projects
                    </td>
                  </tr>
                ) : projectsLoading ? (
                  <tr>
                    <td colSpan={7} className="text-empty">Loading…</td>
                  </tr>
                ) : allProjects.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-empty">No projects found for this organization</td>
                  </tr>
                ) : (
                  visibleProjects.map((p) => (
                    <tr key={p._id}>
                      {PROJECT_COLS.map(({ key, mono, maxw }) => (
                        <td key={key} className={[mono ? "td--mono" : "", maxw || ""].join(" ").trim()}>
                          <div className="td--truncate">
                            {key === "org_name"
                              ? (p[key] || selectedOrg?.org_name || "—")
                              : (p[key] || "—")}
                          </div>
                        </td>
                      ))}
                      <td>
                        <span className={statusBadgeClass(p.status)}>{p.status || "—"}</span>
                      </td>
                      <td>
                        <button className="btn-logs">View Logs</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Load More / Collapse */}
          {selectedOrg && allProjects.length > DASHBOARD_PROJECT_LIMIT && (
            <div className="project-load-more">
              {hasMore ? (
                <button
                  className="load-more-btn"
                  onClick={() => setVisibleCount((v) => v + LOAD_MORE_STEP)}
                >
                  <ChevronDown size={14} />
                  Load More
                  <span className="load-more-btn__count">
                    {allProjects.length - visibleCount} remaining
                  </span>
                </button>
              ) : (
                <button
                  className="load-more-btn load-more-btn--collapse"
                  onClick={() => setVisibleCount(DASHBOARD_PROJECT_LIMIT)}
                >
                  <ChevronUp size={14} />
                  Show Less
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Org Modal only ── */}
      {showOrgModal && (
        <OrgDirectoryModal
          onClose={() => setShowOrgModal(false)}
          orgs={orgs}
          loading={orgsLoading}
          pagination={orgsPagination}
          onPageChange={handleOrgPageChange}
          selectedOrg={selectedOrg}
          onSelectOrg={handleSelectOrg}
        />
      )}
    </div>
  );
};

export default SuperadminDashboard;